<template>
  <div
    class="market"
    data-testid="slide-marketplace"
    role="region"
    aria-label="Slide marketplace"
  >
    <transition name="market-view" mode="out-in">
      <div v-if="view === 'home'" key="home" class="market-home">
        <!-- Editor's pick banner — full-width hero above the
             sidebar/grid. Click opens detail; CTA short-circuits to
             add. Hidden while searching so the grid is uncluttered. -->
        <div
          v-if="featured && !query"
          class="market-feature"
          :style="{ '--accent': featured.accent }"
          role="button"
          tabindex="0"
          data-testid="market-feature"
          @click="openDetail(featured)"
          @keydown.enter="openDetail(featured)"
          @keydown.space.prevent="openDetail(featured)"
        >
          <span class="market-feature-icon" aria-hidden="true">
            <span class="material-symbols-rounded filled">{{ featured.icon }}</span>
          </span>
          <span class="market-feature-text">
            <span class="market-feature-eyebrow">
              <span class="material-symbols-rounded filled">auto_awesome</span>
              Editor's pick · {{ featured.name }}
            </span>
            <span class="market-feature-tagline">{{ featured.tagline || featured.desc }}</span>
          </span>
          <button
            type="button"
            class="market-feature-cta"
            data-testid="market-feature-cta"
            @click.stop="onPick(featured)"
          >
            <span class="material-symbols-rounded">add</span>
            <span>Add {{ featured.name }}</span>
          </button>
        </div>

        <div class="market-body">
          <nav
            class="market-sidebar"
            role="tablist"
            aria-label="Categories"
          >
            <button
              v-for="cat in navCategories"
              :key="cat.id"
              type="button"
              role="tab"
              :aria-selected="cat.id === activeCategory"
              class="market-sidebar-tab"
              :class="[
                { '--active': cat.id === activeCategory },
                `--${cat.id}`,
              ]"
              :data-testid="`market-sidebar-${cat.id}`"
              @click="onCategoryChange(cat.id)"
            >
              <span class="material-symbols-rounded market-sidebar-icon">{{ cat.icon }}</span>
              <span class="market-sidebar-label">{{ cat.label }}</span>
            </button>
          </nav>

          <div ref="gridWrap" class="market-grid-wrap">
            <template v-if="query">
              <div v-if="searchResults.length" class="market-grid">
                <SlideTypeCard
                  v-for="slide in searchResults"
                  :key="slide.id"
                  :slide="slide"
                  with-quick-add
                  @select="openDetail"
                  @quick-add="onPick"
                />
              </div>
              <div v-else class="market-empty">
                <span class="material-symbols-rounded">search_off</span>
                <p>Nothing matches “{{ query }}”.</p>
                <button type="button" class="market-empty-clear" @click="$emit('clear-search')">
                  Clear search
                </button>
              </div>
            </template>

            <template v-else>
              <div v-if="visibleSlides.length" class="market-grid">
                <SlideTypeCard
                  v-for="slide in visibleSlides"
                  :key="slide.id"
                  :slide="slide"
                  with-quick-add
                  @select="openDetail"
                  @quick-add="onPick"
                />
              </div>
              <div v-else class="market-empty">
                <span class="material-symbols-rounded">inventory_2</span>
                <p>No slides in this category yet.</p>
              </div>
            </template>
          </div>
        </div>
      </div>

      <div v-else-if="view === 'detail' && detailSlide" key="detail" class="market-detail-page">
        <div class="market-detail-hero" :style="{ '--accent': detailSlide.accent }">
          <div class="market-detail-hero-bg"></div>
          <div class="market-detail-hero-icon">
            <span class="material-symbols-rounded filled">{{ detailSlide.icon }}</span>
          </div>
          <div class="market-detail-hero-text">
            <span class="market-detail-eyebrow">
              <span class="material-symbols-rounded">{{ sectionIcon(detailSlide.sectionKey) }}</span>
              {{ detailSlide.sectionLabel }}
              <span v-if="detailSlide.isNew" class="market-detail-new">New</span>
            </span>
            <h2 class="market-detail-title">
              {{ detailSlide.name }}
            </h2>
            <p v-if="detailSlide.tagline || detailSlide.desc" class="market-detail-tagline">
              {{ detailSlide.tagline || detailSlide.desc }}
            </p>
            <div class="market-detail-actions">
              <button
                type="button"
                class="market-detail-cta"
                data-testid="detail-add-to-deck"
                :aria-label="`Add ${detailSlide.name} to deck`"
                @click="onPick(detailSlide)"
              >
                <span class="material-symbols-rounded">add</span>
                <span>Add</span>
              </button>
              <button
                v-if="!isInPickerNow"
                type="button"
                class="market-detail-fav market-detail-fav--add"
                data-testid="detail-add-to-bookmarks"
                @click="addBookmarkHere"
              >
                <span class="material-symbols-rounded">bookmark_border</span>
                <span>Add to My Bookmarks</span>
              </button>
              <button
                v-else
                type="button"
                class="market-detail-fav market-detail-fav--remove"
                data-testid="detail-remove-from-bookmarks"
                @click="removeBookmarkHere"
              >
                <span class="material-symbols-rounded filled">bookmark</span>
                <span>Remove from My Bookmarks</span>
              </button>
            </div>
          </div>
        </div>

        <div class="market-detail-body">
          <div v-if="detailSlide.tagline" class="market-detail-section market-detail-desc">
            <p>{{ detailSlide.desc }}</p>
          </div>

          <section class="market-detail-section market-detail-section--preview">
            <h3 class="market-detail-section-title">
              How it works
            </h3>
            <SlidePreview :slide="detailSlide" />
          </section>

          <section v-if="detailUseCases.length" class="market-detail-section">
            <h3 class="market-detail-section-title">
              What it's good for
            </h3>
            <ul class="market-detail-uses">
              <li v-for="(use, i) in detailUseCases" :key="i">
                <span class="market-detail-check material-symbols-rounded">check</span>
                <span>{{ use }}</span>
              </li>
            </ul>
          </section>

          <section v-if="detailSlide.example" class="market-detail-section market-detail-example">
            <h3 class="market-detail-section-title">
              Try it for…
            </h3>
            <p class="market-detail-example-text">
              “{{ detailSlide.example }}”
            </p>
          </section>

          <section v-if="detailSimilar.length" class="market-detail-section market-detail-section--similar">
            <h3 class="market-detail-section-title">
              If you like this, try also
            </h3>
            <div class="market-detail-similar">
              <button
                v-for="alt in detailSimilar"
                :key="alt.id"
                type="button"
                class="market-detail-similar-card"
                @click="openDetail(alt)"
              >
                <span class="market-detail-similar-icon" :style="{ '--accent': alt.accent }">
                  <span class="material-symbols-rounded filled">{{ alt.icon }}</span>
                </span>
                <span class="market-detail-similar-info">
                  <span class="market-detail-similar-name">{{ alt.name }}</span>
                  <span class="market-detail-similar-desc">{{ alt.desc }}</span>
                </span>
                <span class="material-symbols-rounded market-detail-similar-arrow">arrow_forward</span>
              </button>
            </div>
          </section>
        </div>
      </div>
    </transition>
  </div>
</template>

<script lang="ts">
import Vue from 'vue'
import {
  SLIDE_CATALOG, DEFAULT_FAVORITE_IDS, FEATURED_ID, findSlide,
  type SlideType, type SectionKey,
} from '../data/slideTypeCatalog'
import { CATEGORIES, type CategoryId } from '../data/categories'
import { searchSlides } from '../lib/slideSearch'
import {
  addSlideToPicker, removeSlideFromPicker,
  isInPicker as slideIsInPicker,
} from '../lib/pinnedStore'
import { confirmRemoveFromPicker } from '../lib/confirmStore'
import { showToast } from '../lib/toastStore'
import SlidePreview from './SlidePreview.vue'
import SlideTypeCard from './SlideTypeCard.vue'

type View = 'home' | 'detail'

type NavId = 'recommended' | CategoryId

type NavCategory = { id: NavId; label: string; icon: string }

const SECTION_ICONS: Record<SectionKey, string> = {
  quiz: 'sports_esports',
  poll: 'poll',
  content: 'article',
}

// Left-sidebar nav. 'recommended' is a synthetic category that
// surfaces the DEFAULT_FAVORITE_IDS 10. Bookmarks live on a sibling
// top-level tab in the dialog header, so they're skipped here.
const NAV_CATEGORIES: ReadonlyArray<NavCategory> = [
  { id: 'recommended', label: 'Recommended', icon: 'recommend' },
  ...CATEGORIES
    .filter((c) => c.id !== 'favorites')
    .map((c) => ({ id: c.id as NavId, label: c.label, icon: c.icon })),
]

const ACTIVE_CATEGORY_KEY = 'aha:slide-picker-demo:activeCategory'
const DEFAULT_CATEGORY: NavId = 'recommended'

function loadActiveCategory(): NavId {
  try {
    const raw = localStorage.getItem(ACTIVE_CATEGORY_KEY)
    if (raw && NAV_CATEGORIES.some((c) => c.id === raw)) return raw as NavId
  } catch { /* private mode etc. */ }
  return DEFAULT_CATEGORY
}

function saveActiveCategory(id: NavId): void {
  try {
    localStorage.setItem(ACTIVE_CATEGORY_KEY, id)
  } catch { /* private mode etc. */ }
}

const DETAIL_LEAVE_MS = 250

export default Vue.extend({
  name: 'SlideMarketplace',
  components: { SlidePreview, SlideTypeCard },
  props: {
    initialSlideId: { type: String as () => string | null, default: null },
    /** Driven from the dialog-level search bar in App.vue. */
    searchQuery: { type: String, default: '' },
  },
  data() {
    return {
      view: 'home' as View,
      detailSlide: null as SlideType | null,
      activeCategory: loadActiveCategory(),
      totalCount: SLIDE_CATALOG.length,
      detailLeaveTimer: null as number | null,
      navCategories: NAV_CATEGORIES,
    }
  },
  computed: {
    query(): string { return this.searchQuery },
    featured(): SlideType | null {
      return findSlide(FEATURED_ID) ?? null
    },
    /**
     * Slides shown in the grid for the active category.
     * - 'recommended' resolves to the curated DEFAULT_FAVORITE_IDS in
     *   popularity order.
     * - 'all' is every slide in the catalog.
     * - Any other id filters the catalog by that intent category.
     */
    visibleSlides(): SlideType[] {
      if (this.activeCategory === 'recommended') {
        return DEFAULT_FAVORITE_IDS
          .map((id) => findSlide(id))
          .filter((s): s is SlideType => Boolean(s))
      }
      if (this.activeCategory === 'all') return SLIDE_CATALOG
      return SLIDE_CATALOG.filter((s) => s.categories.includes(this.activeCategory as CategoryId))
    },
    searchResults(): SlideType[] {
      return searchSlides(SLIDE_CATALOG, this.query)
    },
    detailUseCases(): string[] { return this.detailSlide?.useCases ?? [] },
    detailSimilar(): SlideType[] {
      if (!this.detailSlide?.similarTo) return []
      return this.detailSlide.similarTo
        .map((id) => findSlide(id))
        .filter((s): s is SlideType => Boolean(s))
    },
    isInPickerNow(): boolean {
      return !!this.detailSlide && slideIsInPicker(this.detailSlide)
    },
  },
  watch: {
    view(newView: View) {
      this.$emit('view-change', newView)
    },
    searchQuery(value: string) {
      // Typing while on the detail page should kick the user back to
      // home so they see the filtered grid.
      if (value && this.view === 'detail') this.goHome()
    },
  },
  mounted() {
    if (this.initialSlideId) {
      const slide = findSlide(this.initialSlideId)
      if (slide) this.openDetail(slide)
    }
    this.$emit('view-change', this.view)
  },
  beforeDestroy() {
    if (this.detailLeaveTimer) window.clearTimeout(this.detailLeaveTimer)
  },
  methods: {
    sectionIcon(key: SectionKey): string {
      return SECTION_ICONS[key]
    },
    onCategoryChange(id: NavId) {
      this.activeCategory = id
      saveActiveCategory(id)
      this.$nextTick(() => {
        const wrap = this.$refs.gridWrap as HTMLElement | undefined
        if (wrap) wrap.scrollTop = 0
      })
    },
    openDetail(slide: SlideType) {
      this.detailSlide = slide
      this.view = 'detail'
    },
    goHome() {
      this.view = 'home'
      if (this.detailLeaveTimer) window.clearTimeout(this.detailLeaveTimer)
      this.detailLeaveTimer = window.setTimeout(() => {
        this.detailSlide = null
        this.detailLeaveTimer = null
      }, DETAIL_LEAVE_MS)
    },
    onPick(slide: SlideType) {
      this.$emit('select', slide)
    },
    addBookmarkHere() {
      if (!this.detailSlide) return
      const slide = this.detailSlide
      addSlideToPicker(slide)
      showToast({
        before: 'Added',
        emphasis: slide.name,
        after: 'to My Bookmarks',
        icon: 'bookmark',
        iconColor: 'var(--aha-pink)',
      })
    },
    async removeBookmarkHere() {
      if (!this.detailSlide) return
      const slide = this.detailSlide
      const ok = await confirmRemoveFromPicker(slide.name)
      if (!ok) return
      removeSlideFromPicker(slide)
      showToast({
        before: 'Removed',
        emphasis: slide.name,
        after: 'from My Bookmarks',
        icon: 'bookmark_remove',
        iconColor: 'var(--aha-error)',
      })
    },
  },
})
</script>

<style scoped lang="scss">
.market {
  --ease-out: cubic-bezier(0.23, 1, 0.32, 1);
  --ease-drawer: cubic-bezier(0.32, 0.72, 0, 1);
  width: 100%;
  height: 100%;
  background: #FAFAFB;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  font-family: 'Plus Jakarta Sans', system-ui, sans-serif;
  position: relative;
  min-width: 0;
  container-type: inline-size;
  container-name: market;
}

.market-view-enter-active, .market-view-leave-active {
  transition:
    opacity 220ms var(--ease-out),
    transform 240ms var(--ease-drawer);
}
.market-view-enter-from, .market-view-enter,
.market-view-leave-to {
  opacity: 0; transform: translateY(6px);
}

.market-home {
  display: flex;
  flex-direction: column;
  min-height: 0;
  flex: 1;
  overflow: hidden;
  padding: 14px 16px 0;
  gap: 12px;
}

.market-body {
  display: grid;
  grid-template-columns: 168px 1fr;
  min-height: 0;
  flex: 1;
  overflow: hidden;
}

.market-sidebar {
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 4px 8px 12px 0;
  overflow-y: auto;
  scrollbar-width: thin;
  scrollbar-color: rgba(15, 15, 20, 0.18) transparent;
  border-right: 1px solid rgba(15, 15, 20, 0.06);
}
.market-sidebar::-webkit-scrollbar { width: 6px; }
.market-sidebar::-webkit-scrollbar-thumb { background: rgba(15, 15, 20, 0.18); border-radius: 3px; }

.market-sidebar-tab {
  display: grid;
  grid-template-columns: 18px 1fr;
  align-items: center;
  gap: 10px;
  padding: 8px 12px;
  border: 0;
  background: transparent;
  border-radius: 8px;
  font-family: inherit;
  font-size: 13px;
  font-weight: 600;
  color: rgba(15, 15, 20, 0.75);
  cursor: pointer;
  text-align: left;
  transition:
    background 160ms var(--ease-out),
    color 160ms var(--ease-out);
}
.market-sidebar-tab:hover {
  background: rgba(106, 30, 187, 0.07);
  color: var(--aha-purple);
}
.market-sidebar-tab:hover .market-sidebar-icon { color: var(--aha-purple); }
.market-sidebar-tab:focus-visible {
  outline: none;
  box-shadow: 0 0 0 2px rgba(106, 30, 187, 0.3);
}
.market-sidebar-icon {
  font-size: 18px;
  color: rgba(15, 15, 20, 0.45);
  transition: color 160ms var(--ease-out);
}
.market-sidebar-label {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  min-width: 0;
}
.market-sidebar-tab.--active {
  background: var(--aha-purple);
  color: white;
}
.market-sidebar-tab.--active .market-sidebar-icon { color: white; }

.market-grid-wrap {
  overflow-y: auto;
  padding: 4px 4px 16px 16px;
  scrollbar-width: thin;
  scrollbar-color: rgba(15, 15, 20, 0.18) transparent;
  min-height: 0;
}
.market-grid-wrap::-webkit-scrollbar { width: 6px; }
.market-grid-wrap::-webkit-scrollbar-thumb { background: rgba(15, 15, 20, 0.18); border-radius: 3px; }

/* Editor's-pick banner — accent-tinted card that anchors the
   marketplace home view. Clicking opens detail; the inline Add CTA
   short-circuits straight into the deck. Background uses the
   featured slide's accent so each featured slide colours its own row. */
.market-feature {
  margin: 0 0 14px;
  padding: 12px 14px;
  border-radius: 12px;
  background: color-mix(in srgb, var(--accent) 8%, #fff);
  display: grid;
  grid-template-columns: 44px 1fr auto;
  align-items: center;
  gap: 14px;
  cursor: pointer;
  border: 0;
  text-align: left;
  transition:
    background 160ms var(--ease-out),
    transform 160ms var(--ease-out);
}
.market-feature:hover {
  transform: translateY(-1px);
  background: color-mix(in srgb, var(--accent) 14%, #fff);
}
.market-feature:focus-visible {
  outline: none;
  box-shadow: 0 0 0 3px color-mix(in srgb, var(--accent) 35%, transparent);
}
.market-feature-icon {
  width: 44px; height: 44px;
  border-radius: 10px;
  background: #fff;
  color: var(--accent);
  display: grid; place-items: center;
}
.market-feature-icon .material-symbols-rounded { font-size: 26px; }
.market-feature-text { display: grid; gap: 2px; min-width: 0; }
.market-feature-eyebrow {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  font-weight: 700;
  color: var(--accent);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.market-feature-eyebrow .material-symbols-rounded { font-size: 14px; }
.market-feature-tagline {
  font-size: 12px;
  line-height: 1.4;
  color: rgba(15, 15, 20, 0.65);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.market-feature-cta {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 14px;
  border: 0;
  border-radius: 10px;
  background: var(--aha-purple);
  color: #fff;
  font-family: inherit;
  font-size: 12px;
  font-weight: 700;
  cursor: pointer;
  transition:
    background 200ms var(--ease-out),
    transform 160ms var(--ease-out);
}
.market-feature-cta:hover {
  background: var(--aha-pink);
  transform: translateY(-1px);
}
.market-feature-cta .material-symbols-rounded { font-size: 14px; }

.market-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 14px;
}

.market-empty {
  display: grid; place-items: center; gap: 8px;
  padding: 56px 16px; color: rgba(15, 15, 20, 0.5); text-align: center;
}
.market-empty .material-symbols-rounded { font-size: 36px; color: rgba(15, 15, 20, 0.25); }
.market-empty p { margin: 0; font-size: 14px; }
.market-empty-clear {
  margin-top: 4px; border: 0;
  background: rgba(106, 30, 187, 0.1); color: #6A1EBB;
  border-radius: 8px; padding: 8px 14px;
  font-family: inherit; font-size: 13px; font-weight: 600;
  cursor: pointer; transition: background 160ms cubic-bezier(0.23, 1, 0.32, 1);
}
.market-empty-clear:hover { background: rgba(106, 30, 187, 0.16); }

/* ============== DETAIL PAGE (full-page in marketplace) ============== */
.market-detail-page {
  display: flex;
  flex-direction: column;
  overflow: hidden;
  min-height: 0;
  flex: 1;
}

.market-detail-hero {
  position: relative;
  padding: 24px 36px;
  display: grid;
  grid-template-columns: auto 1fr;
  align-items: center;
  gap: 28px;
  overflow: hidden;
  flex-shrink: 0;
}
.market-detail-hero-bg {
  position: absolute; inset: 0;
  background: color-mix(in srgb, var(--accent) 10%, white);
}
.market-detail-hero-icon {
  position: relative;
  width: 112px; height: 112px;
  border-radius: 26px;
  background: white;
  color: var(--accent);
  display: grid; place-items: center;
  box-shadow:
    0 1px 0 rgba(15, 15, 20, 0.04),
    0 24px 48px -16px color-mix(in srgb, var(--accent) 45%, transparent);
}
.market-detail-hero-icon .material-symbols-rounded { font-size: 64px; }
.market-detail-hero-text { position: relative; max-width: 60ch; }
.market-detail-eyebrow {
  display: inline-flex; align-items: center; gap: 6px;
  font-size: 11px; font-weight: 700; letter-spacing: 0.08em; text-transform: uppercase;
  color: var(--accent); margin-bottom: 8px;
}
.market-detail-eyebrow .material-symbols-rounded { font-size: 14px; }
.market-detail-new {
  font-size: 10px; font-weight: 700; letter-spacing: 0;
  background: var(--aha-pink); color: white;
  padding: 2px 7px; border-radius: 4px; text-transform: none;
}
.market-detail-title {
  font-size: 32px; font-weight: 800; margin: 0 0 8px;
  letter-spacing: -0.015em; color: #1A1A2E;
}
.market-detail-tagline {
  font-size: 16px; line-height: 1.45; color: rgba(15, 15, 20, 0.7);
  margin: 0 0 20px;
}
.market-detail-actions { display: flex; gap: 10px; flex-wrap: wrap; align-items: center; }
.market-detail-cta {
  display: inline-flex; align-items: center; gap: 8px;
  padding: 12px 20px;
  border: 0; border-radius: 12px;
  background: var(--aha-purple); color: white;
  font-family: inherit; font-size: 14px; font-weight: 700;
  cursor: pointer;
  transition: background 200ms var(--ease-out), transform 160ms var(--ease-out), box-shadow 200ms var(--ease-out);
}
.market-detail-cta:hover {
  background: var(--aha-pink);
  transform: translateY(-1px);
  box-shadow: 0 12px 24px -8px rgba(255, 64, 129, 0.5);
}
.market-detail-cta .material-symbols-rounded { font-size: 18px; }

.market-detail-fav {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 11px 16px;
  border-radius: 12px;
  font-family: inherit;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  transition:
    background 160ms var(--ease-out),
    color 160ms var(--ease-out),
    border-color 160ms var(--ease-out);
}
.market-detail-fav .material-symbols-rounded { font-size: 18px; }
.market-detail-fav--add {
  background: white;
  color: rgba(15, 15, 20, 0.78);
  border: 1px solid rgba(15, 15, 20, 0.16);
}
.market-detail-fav--add .material-symbols-rounded { color: var(--aha-pink); }
.market-detail-fav--add:hover {
  background: rgba(255, 64, 129, 0.06);
  border-color: rgba(255, 64, 129, 0.45);
  color: #1A1A2E;
}
.market-detail-fav--remove {
  background: rgba(255, 64, 129, 0.10);
  color: var(--aha-pink);
  border: 1px solid rgba(255, 64, 129, 0.35);
}
.market-detail-fav--remove .material-symbols-rounded { color: var(--aha-pink); }
.market-detail-fav--remove:hover {
  background: var(--aha-error-tint);
  color: var(--aha-error);
  border-color: rgba(230, 0, 92, 0.35);
}
.market-detail-fav--remove:hover .material-symbols-rounded { color: var(--aha-error); }

.market-detail-body {
  overflow-y: auto;
  padding: 28px 36px;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
  column-gap: 32px;
  row-gap: 24px;
  scrollbar-width: thin;
  scrollbar-color: rgba(15, 15, 20, 0.18) transparent;
  flex: 1;
  min-height: 0;
}
.market-detail-body::-webkit-scrollbar { width: 6px; }
.market-detail-body::-webkit-scrollbar-thumb { background: rgba(15, 15, 20, 0.18); border-radius: 3px; }

.market-detail-section { min-width: 0; }
.market-detail-section--preview { grid-column: 1 / -1; }
.market-detail-section--similar { grid-column: 1 / -1; }
.market-detail-desc {
  grid-column: 1 / -1;
  font-size: 15px; line-height: 1.55; color: rgba(15, 15, 20, 0.65);
  padding: 0;
  background: transparent;
  border: 0;
  margin: 0;
}
.market-detail-desc p { margin: 0; }
.market-detail-section-title {
  font-size: 11px; font-weight: 700; letter-spacing: 0.08em; text-transform: uppercase;
  color: rgba(15, 15, 20, 0.45); margin: 0 0 12px;
}
.market-detail-uses { list-style: none; margin: 0; padding: 0; display: flex; flex-direction: column; gap: 10px; }
.market-detail-uses li {
  display: grid;
  grid-template-columns: 22px 1fr;
  gap: 10px;
  font-size: 14px; line-height: 1.5;
  color: #1A1A2E;
}
.market-detail-check {
  font-size: 18px;
  color: #10b981;
  background: rgba(16, 185, 129, 0.12);
  border-radius: 999px;
  padding: 2px;
  width: 22px; height: 22px;
  display: grid; place-items: center;
}
.market-detail-example {
  background: var(--aha-blush);
  border: 1px solid rgba(106, 30, 187, 0.16);
  border-radius: 12px;
  padding: 16px 18px;
}
.market-detail-example .market-detail-section-title { color: #6A1EBB; }
.market-detail-example-text {
  font-size: 14px; line-height: 1.5;
  color: #1A1A2E; margin: 0; font-style: italic;
}
.market-detail-similar {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 10px;
}
.market-detail-similar-card {
  display: grid;
  grid-template-columns: 40px 1fr auto;
  gap: 12px;
  align-items: center;
  padding: 12px;
  border: 1px solid rgba(15, 15, 20, 0.06);
  border-radius: 12px;
  background: white;
  cursor: pointer;
  text-align: left;
  font-family: inherit;
  transition: transform 160ms var(--ease-out), border-color 160ms var(--ease-out), background 160ms var(--ease-out);
}
.market-detail-similar-card:hover {
  transform: translateY(-1px);
  border-color: rgba(106, 30, 187, 0.4);
  background: var(--aha-purple-tint);
}
.market-detail-similar-icon {
  width: 40px; height: 40px;
  border-radius: 10px;
  background: color-mix(in srgb, var(--accent) 12%, white);
  color: var(--accent);
  display: grid; place-items: center;
}
.market-detail-similar-icon .material-symbols-rounded { font-size: 22px; }
.market-detail-similar-info { display: flex; flex-direction: column; gap: 2px; min-width: 0; }
.market-detail-similar-name { font-size: 14px; font-weight: 600; color: #1A1A2E; }
.market-detail-similar-desc {
  font-size: 12px; color: rgba(15, 15, 20, 0.55);
  display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden;
}
.market-detail-similar-arrow { font-size: 18px; color: rgba(15, 15, 20, 0.35); }

/* Narrow marketplace adaptations (mobile / small viewports). */
@container market (max-width: 900px) {
  .market-grid { grid-template-columns: repeat(3, minmax(0, 1fr)); }
}
@container market (max-width: 720px) {
  .market-body { grid-template-columns: 160px 1fr; }
  .market-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); }
  .market-detail-hero { padding: 22px 22px; gap: 18px; }
  .market-detail-hero-icon { width: 84px; height: 84px; border-radius: 20px; }
  .market-detail-hero-icon .material-symbols-rounded { font-size: 48px; }
  .market-detail-title { font-size: 24px; }
  .market-detail-body { padding: 20px 22px; }
  .market-grid-wrap { padding: 14px 16px; }
}
@container market (max-width: 540px) {
  .market-home { padding: 10px 12px 0; gap: 10px; }
  .market-body {
    grid-template-columns: 1fr;
    grid-template-rows: auto 1fr;
  }
  .market-sidebar {
    flex-direction: row;
    overflow-x: auto;
    overflow-y: hidden;
    border-right: 0;
    border-bottom: 1px solid rgba(15, 15, 20, 0.06);
    padding: 6px 0;
    gap: 6px;
  }
  .market-sidebar-tab {
    flex-shrink: 0;
    grid-template-columns: 16px auto;
    padding: 6px 10px;
    font-size: 12px;
  }
  .market-grid { grid-template-columns: 1fr; }
  .market-detail-hero { grid-template-columns: 1fr; gap: 14px; }
  .market-detail-hero-text { max-width: 100%; }
  .market-feature { grid-template-columns: 36px 1fr; gap: 10px; padding: 10px 12px; }
  .market-feature-cta { grid-column: 1 / -1; justify-self: end; }
  .market-feature-cta:hover { transform: none; }
}

@media (prefers-reduced-motion: reduce) {
  .market-detail-cta:hover { transform: none; }
  .market-view-enter-active, .market-view-leave-active { transition: opacity 160ms ease; }
  .market-view-enter-from, .market-view-leave-to { transform: none; }
}
</style>
